import { Fragment, useContext } from 'react';
import { generatePath, useParams, useRouteMatch } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { StoreContext } from 'index';
import useTabIndex from 'hooks/utils/useTabIndex';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import { useSessionsContext } from 'contexts/SessionsContext';
import {
  ROUTE_SESSIONS_ENTITY,
  ROUTE_SESSIONS_ENTITY_RECURRING,
  ROUTE_SESSIONS_ENTITY_SESSIONS,
  ROUTE_SESSIONS_ENTITY_TEMPLATES
} from 'routes/RouteList';

import messages from 'messages';

import ContextMenu from 'components/menu/ContextMenu';
import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import {
  ActionButton,
  Panel,
  PanelBody,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper,
  RefactoredPanelHeader
} from 'components/panel';
import { TabList, TabPanel, TabPanelBody, Tabs } from 'components/tabs';
import TabLink from 'components/tabs/TabLink';

import RetryPanel from 'containers/partials/error-boundary/RetryPanel';
import OverviewSessionsTab from 'containers/pages/sessions/OverviewSessionsTab';
import OverviewTemplatesTab from 'containers/pages/sessions/OverviewTemplatesTab';
import OverviewRecurringTab from 'containers/pages/sessions/OverviewRecurringTab';

const Overview = () => {
  const {
    authStore: { user, entity }
  } = useContext(StoreContext);
  const { entityId } = useParams();
  const ability = useAbility(AbilityContext);
  const sessionTabs = [ROUTE_SESSIONS_ENTITY_SESSIONS];

  const active = useRouteMatch([ROUTE_SESSIONS_ENTITY]);

  if (ability.can('create', 'Prevention')) {
    sessionTabs.push(ROUTE_SESSIONS_ENTITY_TEMPLATES);
  }
  if (ability.can('create', 'RecurringSessions')) {
    sessionTabs.push(ROUTE_SESSIONS_ENTITY_RECURRING);
  }

  const [tabIndex, setTabIndex] = useTabIndex(sessionTabs, false);
  const { sessions, error } = useSessionsContext();

  return (
    <>
      <Panel active={active.isExact}>
        {error && <RetryPanel />}

        {sessions && (
          <Fragment>
            <RefactoredPanelHeader>
              <PanelTitleWrapper>
                <PanelTitle>
                  <FormattedMessage {...messages.titleSessions} />
                </PanelTitle>
                <PanelSubtitle>
                  <FormattedMessage
                    {...messages.subtitleSessions}
                    values={{
                      name: user.getEntityName(entity)
                    }}
                  />
                </PanelSubtitle>
              </PanelTitleWrapper>

              <PanelTitleButtonsGroup>
                <ActionButton />
                <ContextMenuWrapper trigger={<PanelTitleButton />}>
                  <ContextMenu />
                </ContextMenuWrapper>
              </PanelTitleButtonsGroup>
            </RefactoredPanelHeader>
            <PanelBody>
              <Tabs
                selectedIndex={tabIndex}
                onSelect={index => setTabIndex(index)}
              >
                <TabList>
                  <TabLink
                    key="sessions.sessions"
                    to={generatePath(ROUTE_SESSIONS_ENTITY, {
                      entityId,
                      sessionsTab: 'sessions'
                    })}
                  >
                    <FormattedMessage {...messages.tabSessionsSessions} />
                  </TabLink>
                  {ability.can('create', 'Prevention') && (
                    <TabLink
                      key="prevention.templates"
                      to={generatePath(ROUTE_SESSIONS_ENTITY_TEMPLATES, {
                        entityId,
                        sessionsTab: 'templates'
                      })}
                    >
                      <FormattedMessage {...messages.tabSessionsTemplates} />
                    </TabLink>
                  )}
                  {ability.can('create', 'RecurringSessions') && (
                    <TabLink
                      key="recurring.sessions"
                      to={generatePath(ROUTE_SESSIONS_ENTITY_RECURRING, {
                        entityId,
                        sessionsTab: 'recurring'
                      })}
                    >
                      <FormattedMessage {...messages.tabRecurringSessions} />
                    </TabLink>
                  )}
                </TabList>
                <TabPanel key="sessions.sessions">
                  <TabPanelBody>
                    <OverviewSessionsTab />
                  </TabPanelBody>
                </TabPanel>
                {ability.can('create', 'Prevention') && (
                  <TabPanel key="prevention.templates">
                    <TabPanelBody>
                      <OverviewTemplatesTab />
                    </TabPanelBody>
                  </TabPanel>
                )}
                {ability.can('create', 'RecurringSessions') && (
                  <TabPanel key="recurring.sessions">
                    <TabPanelBody>
                      <OverviewRecurringTab />
                    </TabPanelBody>
                  </TabPanel>
                )}
              </Tabs>
            </PanelBody>
          </Fragment>
        )}
      </Panel>
    </>
  );
};

export default Overview;
